import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef } from 'react'

import { useNotificationUi } from './hooks/useNotificationUi'

import { MessageStyled, NotificationStyled, TitleStyled } from './styles'

import { NotificationInfo } from '../interfaces/notification'

export type StyleAlert = 'error' | 'info' | 'warning' | 'success' | 'none' | undefined

type NotificationUiProps = NotificationInfo

// Los errores y advertencias requieren más tiempo de lectura que las confirmaciones
const DURACION_POR_TIPO: Record<string, number> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: 8000,
}

const NotificationUi: React.FC<NotificationUiProps> = ({ id, message, style = 'info', title, repeticiones, reiniciadoEn }) => {
  const { isExiting, onClose, handleExitComplete } = useNotificationUi(
    id,
    DURACION_POR_TIPO[style] ?? 5000,
    reiniciadoEn,
  )

  // El mismo aviso llegó varias veces mientras seguía visible: se muestra una sola vez
  // con el número de repeticiones, en lugar de apilar toasts idénticos.
  const veces = repeticiones ?? 1
  const tituloConContador = veces > 1 ? `${title} (${veces})` : title

  const contenedorRef = useRef<HTMLDivElement>(null)

  /*
   * Kendo pinta el cierre como `createElement('span', { className: 'k-notification-close-action',
   * title: 'Close', onClick })`: un span con onClick, sin role, sin tabIndex y sin aria-label. No
   * se alcanza con teclado y un lector de pantalla no ve nada -el paquete no tiene un solo
   * aria-label-. No podemos cambiar ese markup, asi que se le agregan los atributos que le faltan
   * despues de montarlo, y una tecla que dispare el mismo onClick que ya trae.
   */
  useEffect(() => {
    const cierre = contenedorRef.current?.querySelector<HTMLElement>('.k-notification-close-action')

    if (!cierre) {
      return
    }

    cierre.setAttribute('role', 'button')
    cierre.setAttribute('tabindex', '0')
    cierre.setAttribute('aria-label', 'Cerrar aviso')

    const alPresionar = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        cierre.click()
      }
    }

    cierre.addEventListener('keydown', alPresionar)

    return () => cierre.removeEventListener('keydown', alPresionar)
  }, [])

  // Manejar cierre manual directamente sin animación Framer Motion
  const handleManualClose = (): void => {
    // Remover inmediatamente del Redux para evitar conflicto de animaciones
    onClose()
  }

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {!isExiting && (
        <motion.div
          ref={contenedorRef}
          /*
           * Sin esto los avisos no se anunciaban nunca: quien usa lector de pantalla no se
           * enteraba ni de un error. `assertive` para errores y advertencias, que interrumpen lo
           * que se este leyendo; `polite` para el resto, que espera una pausa.
           */
          role={style === 'error' || style === 'warning' ? 'alert' : 'status'}
          aria-live={style === 'error' || style === 'warning' ? 'assertive' : 'polite'}
          initial={{ opacity: 0, x: 60, scale: 0.98 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 60, scale: 0.98 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <NotificationStyled type={{ style, icon: true }} closable onClose={handleManualClose}>
            <TitleStyled>{tituloConContador}</TitleStyled>
            <MessageStyled>{message}</MessageStyled>
          </NotificationStyled>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default NotificationUi
